import { CrmPermissionCode, PermissionAction } from '@/services/authentication/model'
import { FeatureKey } from '@/utils/helper/feature-visibility'
import { JSX, lazy } from 'react'

export enum RouteType {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

export interface RoutePermission {
  code: CrmPermissionCode
  action: PermissionAction
}

interface IRoutesList {
  route_type: string
  path: string
  component: React.LazyExoticComponent<() => JSX.Element>
  private?: boolean
  feature?: FeatureKey
  permission?: RoutePermission
  /**
   * QTY-2069/2072: additionally restricted to the sales chain of command
   * (club owner, club manager, CS & Sale manager). ANDed with `permission` —
   * the sales portfolio and renewal forecast share `CrmSalesList` with the
   * sale list, so the permission code alone can't separate them.
   */
  salesTeamLeadOnly?: boolean
}

/** Shorthand to avoid repeating PermissionAction.READ everywhere */
const read = (code: CrmPermissionCode): RoutePermission => ({ code, action: PermissionAction.READ })

const routes: IRoutesList[] = [
  {
    route_type: RouteType.PUBLIC,
    path: '/login',
    component: lazy(() => import('../features/authentication/pages/LoginPage')),
    private: false,
  },
  {
    route_type: RouteType.PUBLIC,
    path: '/forget-password',
    component: lazy(() => import('../features/authentication/pages/ForgetPasswordPage')),
    private: false,
  },
  {
    route_type: RouteType.PUBLIC,
    path: '/reset-password',
    component: lazy(() => import('../features/authentication/pages/ResetPasswordPage')),
    private: false,
  },
  {
    route_type: RouteType.PUBLIC,
    path: '/verify-email-change',
    component: lazy(() => import('../features/authentication/pages/VerifyEmailChangePage')),
    private: false,
  },
  // MCP OAuth consent ("Connect" from ChatGPT / Claude). Public and tenant-less
  // so ProtectedRoute never bounces it to /login or /select-tenant; the page
  // signs the user in itself. The API redirects here (MCP_OAUTH_CONSENT_URL).
  {
    route_type: RouteType.PUBLIC,
    path: '/oauth/authorize',
    component: lazy(() => import('../features/authentication/pages/McpConsentPage')),
    private: false,
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/select-tenant',
    component: lazy(() => import('../features/authentication/pages/SelectTenantPage')),
    private: true,
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/check-in',
    component: lazy(() => import('../features/checkin/pages/CheckinPage')),
    permission: read(CrmPermissionCode.CrmMemberCheckIn),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/staff',
    component: lazy(() => import('../features/staff/pages/StaffListPage')),
    permission: read(CrmPermissionCode.CrmStaffList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/staff/create',
    component: lazy(() => import('../features/staff/pages/CreateStaffPage')),
    permission: { code: CrmPermissionCode.CrmStaffList, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/staff/branch-access',
    component: lazy(() => import('../features/staff/pages/BranchAccessPage')),
    permission: read(CrmPermissionCode.CrmStaffList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/staff/:id',
    component: lazy(() => import('../features/staff/pages/StaffDetailPage')),
    permission: read(CrmPermissionCode.CrmStaffList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/class-schedule',
    component: lazy(() => import('../features/class/pages/ClassSchedulePage')),
    permission: read(CrmPermissionCode.CrmClassSchedule),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/class',
    component: lazy(() => import('../features/class/pages/ClassCategoryPage')),
    permission: read(CrmPermissionCode.CrmClassCategories),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/class/:id',
    component: lazy(() => import('../features/class/pages/ClassDetailPage')),
    permission: read(CrmPermissionCode.CrmClassCategories),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/class/:id/schedule/:scheduleId',
    component: lazy(() => import('../features/class/pages/ScheduleDetailPage')),
    permission: read(CrmPermissionCode.CrmClassSchedule),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/payments',
    component: lazy(() => import('../features/receipt/pages/AllPaymentReceiptPage')),
    permission: read(CrmPermissionCode.CrmReceipts),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/recurring-payments',
    component: lazy(() => import('../features/member/pages/RecurringPaymentsPage')),
    permission: read(CrmPermissionCode.CrmReceipts),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/',
    component: lazy(() => import('../features/root/pages/RootPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member-packages',
    component: lazy(() => import('../features/package/pages/PackageListPage')),
    permission: read(CrmPermissionCode.CrmMemberPackages),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/archived-packages',
    component: lazy(() => import('../features/package/pages/ArchivedPackageListPage')),
    permission: read(CrmPermissionCode.CrmArchivedPackages),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member-packages/:id',
    component: lazy(() => import('../features/package/pages/PackageDetailPage')),
    permission: read(CrmPermissionCode.CrmMemberPackages),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member-packages/:id/create',
    component: lazy(() => import('../features/package/pages/CreatePackagePage')),
    permission: { code: CrmPermissionCode.CrmMemberPackages, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member-packages/e-signature/:userId',
    component: lazy(() => import('../features/package/pages/PackageESignaturePage')),
    permission: read(CrmPermissionCode.CrmMemberPackages),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member',
    component: lazy(() => import('../features/member/pages/MemberListPage')),
    permission: read(CrmPermissionCode.CrmMemberList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member/create',
    component: lazy(() => import('../features/member/pages/CreateMemberPage')),
    permission: { code: CrmPermissionCode.CrmMemberList, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member/:id',
    component: lazy(() => import('../features/member/pages/MemberDetailsPage')),
    permission: read(CrmPermissionCode.CrmMemberList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member/:id/member-create/e-signature',
    // component: lazy(() => import('../features/member/pages/NewMemberESignaturePage')),
    component: lazy(() => import('../features/member/pages/NewMemberESignaturePageV2')),
    permission: read(CrmPermissionCode.CrmMemberList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/member/:id/purchase-credit',
    component: lazy(() => import('../features/member/pages/PurchaseCreditPage')),
    feature: 'credit',
    permission: read(CrmPermissionCode.CrmMemberList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/goal-settings',
    component: lazy(() => import('../features/goal-settings/pages/GoalSettingsListPage')),
    permission: read(CrmPermissionCode.CrmPtGoalSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/goal-settings/create',
    component: lazy(() => import('../features/goal-settings/pages/CreateGoalSettingPage')),
    permission: { code: CrmPermissionCode.CrmPtGoalSettings, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/goal-settings/:id',
    component: lazy(() => import('../features/goal-settings/pages/GoalSettingDetailPage')),
    permission: read(CrmPermissionCode.CrmPtGoalSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/goal-settings/:goalId/result-sessions/:sessionId',
    component: lazy(() => import('../features/goal-settings/pages/ResultSessionPage')),
    permission: read(CrmPermissionCode.CrmPtGoalSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/goal-settings/:goalId/result-sessions/:sessionId/:phase/edit',
    component: lazy(() => import('../features/goal-settings/pages/SessionLogEditPage')),
    permission: { code: CrmPermissionCode.CrmPtGoalSettings, action: PermissionAction.UPDATE },
  },
  {
    route_type: RouteType.PUBLIC,
    path: '/docusign/callback',
    component: lazy(() => import('../features/docusign/pages/DocusignOAuthCallback')),
  },
  // Personal Trainer
  {
    route_type: RouteType.PRIVATE,
    path: '/personal-trainer-schedule/list',
    component: lazy(() => import('../features/trainer/pages/TrainerSchedulePage')),
    permission: read(CrmPermissionCode.CrmPtSchedule),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/personal-trainer',
    component: lazy(() => import('../features/trainer/pages/TrainerListPage')),
    permission: read(CrmPermissionCode.CrmPtList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/personal-trainer/:trainerId',
    component: lazy(() => import('../features/trainer-detail/pages/TrainerDetailPage')),
    permission: read(CrmPermissionCode.CrmPtList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/training-program',
    component: lazy(() => import('../features/trainer/pages/TrainingProgramListPage')),
    permission: read(CrmPermissionCode.CrmTrainingProgram),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/session-utilization',
    component: lazy(() => import('../features/trainer/pages/SessionUtilizationPage')),
    permission: read(CrmPermissionCode.CrmPtList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/trainer-commission/create',
    component: lazy(
      () => import('../features/trainer-commission/pages/TrainerCommissionCreatePage')
    ),
    permission: { code: CrmPermissionCode.CrmTrainerConduct, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/trainer-commission/:conditionId',
    component: lazy(
      () => import('../features/trainer-commission/pages/TrainerCommissionDetailPage')
    ),
    permission: read(CrmPermissionCode.CrmTrainerConduct),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/trainer-commission',
    component: lazy(() => import('../features/trainer-commission/pages/TrainerCommissionListPage')),
    permission: read(CrmPermissionCode.CrmTrainerConduct),
  },
  // Swimming & Aquatic
  {
    route_type: RouteType.PRIVATE,
    path: '/swimming-schedule',
    component: lazy(() => import('../features/swimming/pages/AllSwimmingScheduleListPage')),
    permission: read(CrmPermissionCode.CrmSwimmingSchedule),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/swimming-goal-settings',
    component: lazy(() => import('../features/swimming/pages/SwimmingGoalSettingListPage')),
    permission: read(CrmPermissionCode.CrmSwimmingGoalSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/swimming-coach',
    component: lazy(() => import('../features/swimming/pages/SwimmingCoachListPage')),
    permission: read(CrmPermissionCode.CrmSwimmingCoachList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/swimming-coach/:trainerId/details',
    component: lazy(() => import('../features/swimming/pages/SwimmingCoachDetailsPage')),
    permission: read(CrmPermissionCode.CrmSwimmingCoachList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/create-swimming-goal-settings',
    component: lazy(() => import('../features/swimming/pages/SwimmingGoalSettingCreatePage')),
    permission: {
      code: CrmPermissionCode.CrmSwimmingGoalSettings,
      action: PermissionAction.CREATE,
    },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/swimming-goal-settings/:id/member/:memberId',
    component: lazy(() => import('../features/swimming/pages/SwimmingGoalSettingDetailPage')),
    permission: read(CrmPermissionCode.CrmSwimmingGoalSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/swimming-goal-settings/:id/result-session/:sessionId',
    component: lazy(() => import('../features/swimming/pages/SwimmingGoalSettingDetailResultPage')),
    permission: read(CrmPermissionCode.CrmSwimmingGoalSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/swimming-schedule/:id',
    component: lazy(() => import('../features/swimming/pages/SwimmingScheduleDetailPage')),
    permission: read(CrmPermissionCode.CrmSwimmingSchedule),
  },
  // Lead Management
  {
    route_type: RouteType.PRIVATE,
    path: '/new-lead',
    component: lazy(() => import('../features/lead/pages/NewLeadListPage')),
    permission: read(CrmPermissionCode.CrmLeadNewLead),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/up-sell',
    component: lazy(() => import('../features/up-sell/pages/UpSellListPage')),
    permission: read(CrmPermissionCode.CrmLeadUpSell),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/up-sell/:id',
    component: lazy(() => import('../features/up-sell/pages/UpSellMemberDetailPage')),
    permission: read(CrmPermissionCode.CrmLeadUpSell),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/new-lead/:id',
    component: lazy(() => import('../features/lead/pages/NewLeadMemberDetailPage')),
    permission: read(CrmPermissionCode.CrmLeadNewLead),
  },
  // To-do List
  {
    route_type: RouteType.PRIVATE,
    path: '/todo-list-new-lead',
    component: lazy(() => import('../features/lead/pages/TodoListNewLeadListPage')),
    permission: read(CrmPermissionCode.CrmTodoNewLead),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/todo-list-upsell',
    component: lazy(() => import('../features/up-sell/pages/TodoListUpSellListPage')),
    permission: read(CrmPermissionCode.CrmTodoUpSell),
  },
  // Sales Management
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-portfolio',
    component: lazy(() => import('../features/sale/pages/SalePortfolioPage')),
    permission: read(CrmPermissionCode.CrmSalesList),
    salesTeamLeadOnly: true,
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/renewal-forecast',
    component: lazy(() => import('../features/sale/pages/RenewalForecastPage')),
    permission: read(CrmPermissionCode.CrmSalesList),
    salesTeamLeadOnly: true,
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-manage',
    component: lazy(() => import('../features/sale/pages/SaleManagementListPage')),
    permission: read(CrmPermissionCode.CrmSalesList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-todo',
    component: lazy(() => import('../features/sale/pages/SaleTodoListPage')),
    permission: read(CrmPermissionCode.CrmSalesTodoList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-lead-pending',
    component: lazy(() => import('../features/sale/pages/LeadPendingAssignmentPage')),
    permission: read(CrmPermissionCode.CrmLeadAssignment),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-manage/:saleId',
    component: lazy(() => import('../features/sale/pages/SaleDetailPage')),
    permission: read(CrmPermissionCode.CrmSalesList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-commission/create',
    component: lazy(() => import('../features/sale-commission/pages/CreateSaleCommissionRulePage')),
    permission: { code: CrmPermissionCode.CrmSalesCommission, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-commission/:id',
    component: lazy(() => import('../features/sale-commission/pages/SaleCommissionDetailPage')),
    permission: read(CrmPermissionCode.CrmSalesCommission),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-commission',
    component: lazy(() => import('../features/sale-commission/pages/SaleCommissionListPage')),
    permission: read(CrmPermissionCode.CrmSalesCommission),
  },
  // Summary Dashboard
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/member',
    component: lazy(() => import('../features/dashboard/pages/MemberDashboardPage')),
    permission: read(CrmPermissionCode.CrmMemberSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/class',
    component: lazy(() => import('../features/dashboard/pages/ClassSummaryPage')),
    permission: read(CrmPermissionCode.CrmClassSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/trainer',
    component: lazy(() => import('../features/dashboard/pages/TrainerSummaryPage')),
    permission: read(CrmPermissionCode.CrmTrainerSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/swimming',
    component: lazy(() => import('../features/dashboard/pages/SwimmingSummaryPage')),
    permission: read(CrmPermissionCode.CrmSwimmingSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/credit',
    component: lazy(() => import('../features/dashboard/pages/CreditSummaryPage')),
    feature: 'credit',
    permission: read(CrmPermissionCode.CrmCreditSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/loyalty',
    component: lazy(() => import('../features/dashboard/pages/LoyaltyDashboardPage')),
    feature: 'loyalty',
    permission: read(CrmPermissionCode.CrmLoyaltySummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/rewards',
    component: lazy(() => import('../features/dashboard/pages/RewardManagementPage')),
    feature: 'loyalty',
    permission: read(CrmPermissionCode.CrmLoyaltyRedeem),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/redemption-approvals',
    component: lazy(() => import('../features/dashboard/pages/RedemptionApprovalsPage')),
    feature: 'loyalty',
    permission: read(CrmPermissionCode.CrmLoyaltyRedeem),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/checkin',
    component: lazy(() => import('../features/dashboard/pages/DailyCheckInDashboardPage')),
    permission: read(CrmPermissionCode.CrmCheckInSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/sales',
    component: lazy(() => import('../features/dashboard/pages/SalesSummaryDashboardPage')),
    permission: read(CrmPermissionCode.CrmSalesSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/opportunity-analysis',
    component: lazy(() => import('../features/dashboard/pages/OpportunityAnalysisPage')),
    permission: read(CrmPermissionCode.CrmOpportunityAnalysis),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/individual-sale',
    component: lazy(() => import('../features/dashboard/pages/IndividualSaleSummaryPage')),
    permission: read(CrmPermissionCode.CrmIndividualSaleSummary),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/revenue-breakdown',
    component: lazy(() => import('../features/dashboard/pages/RevenueBreakdownPage')),
    permission: read(CrmPermissionCode.CrmRevenueBreakdown),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/member-feedback',
    component: lazy(() => import('../features/dashboard/pages/MemberFeedbackPage')),
    permission: read(CrmPermissionCode.CrmMemberFeedback),
  },
  // Room & Facilities
  {
    route_type: RouteType.PRIVATE,
    path: '/rooms',
    component: lazy(() => import('../features/room/pages/RoomListPage')),
    permission: read(CrmPermissionCode.CrmRoomList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/rooms-availability',
    component: lazy(() => import('../features/room/pages/RoomAvailabilityListPage')),
    permission: read(CrmPermissionCode.CrmRoomAvailability),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/rooms/:id',
    component: lazy(() => import('../features/room/pages/RoomDetailPage')),
    permission: read(CrmPermissionCode.CrmRoomList),
  },
  // Products & Receipts
  {
    route_type: RouteType.PRIVATE,
    path: '/products',
    component: lazy(() => import('../features/product/pages/AllProductListPage')),
    permission: read(CrmPermissionCode.CrmProducts),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/product-sales',
    component: lazy(() => import('../features/product/pages/ProductSalePage')),
    permission: { code: CrmPermissionCode.CrmProducts, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/product-sales/history',
    component: lazy(() => import('../features/product/pages/ProductSalesHistoryPage')),
    permission: read(CrmPermissionCode.CrmProducts),
  },
  // Listed before `/products/:id` for readability only — `<Routes>` ranks by
  // specificity, so the static `/stock` segment wins regardless of array order.
  {
    route_type: RouteType.PRIVATE,
    path: '/products/:id/stock',
    component: lazy(() => import('../features/product/pages/StockDashboardPage')),
    // Its own code, not CrmProducts: this report is toggled per tenant from the
    // commander console, and it seeds disabled — so a tenant that has not turned
    // it on must not reach a page whose API calls would 403.
    permission: read(CrmPermissionCode.CrmStockReport),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/products/:id',
    component: lazy(() =>
      import('../features/product/pages/LegacyProductRedirect').then((module) => ({
        default: module.LegacyProductDetailRedirect,
      }))
    ),
    permission: read(CrmPermissionCode.CrmProducts),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/products/purchase',
    component: lazy(() =>
      import('../features/product/pages/LegacyProductRedirect').then((module) => ({
        default: module.LegacyProductPurchaseRedirect,
      }))
    ),
    permission: { code: CrmPermissionCode.CrmProducts, action: PermissionAction.CREATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/facility',
    component: lazy(() => import('../features/facility/pages/FacilityListPage')),
    permission: read(CrmPermissionCode.CrmFacilityList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/facility/:id',
    component: lazy(() => import('../features/facility/pages/FacilityDetailPage')),
    permission: read(CrmPermissionCode.CrmFacilityList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/facility-availability',
    component: lazy(() => import('../features/facility/pages/FacilityAvailabilityListPage')),
    permission: read(CrmPermissionCode.CrmFacilityAvailability),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/dashboard/facility',
    component: lazy(() => import('../features/dashboard/pages/FacilitySummaryPage')),
    permission: read(CrmPermissionCode.CrmFacilityAvailability),
  },
  // Wellness & Spa — the treatment board, its catalog and its reports. Each path needs its own
  // entry: ProtectedRoute matches with `end: true`, so a prefix guard would leave the rest
  // unguarded.
  //
  // Board and reports are READ; the settings page edits the catalog (treatments, prices,
  // consumables) and takes UPDATE instead — the same read/write split `/products` makes against
  // `/product-sales`, and `/goal-settings` against its edit route. `hasPermission` matches an
  // exact (code, action) pair with no hierarchy, so a read-only role simply never satisfies it.
  {
    route_type: RouteType.PRIVATE,
    path: '/wellness-spa',
    component: lazy(() => import('../features/spa/pages/SpaBoardPage')),
    permission: read(CrmPermissionCode.CrmWellnessSpa),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/wellness-spa/settings',
    component: lazy(() => import('../features/spa/pages/SpaSettingsPage')),
    permission: { code: CrmPermissionCode.CrmWellnessSpa, action: PermissionAction.UPDATE },
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/wellness-spa/reports',
    component: lazy(() => import('../features/spa/pages/SpaReportPage')),
    permission: read(CrmPermissionCode.CrmWellnessSpa),
  },
  // Support Tickets — intentionally permission-less; available to all authed users.
  {
    route_type: RouteType.PRIVATE,
    path: '/support-tickets',
    component: lazy(() => import('../features/support-ticket/pages/SupportTicketListPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/support-tickets/create',
    component: lazy(() => import('../features/support-ticket/pages/CreateSupportTicketPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/support-tickets/:id',
    component: lazy(() => import('../features/support-ticket/pages/SupportTicketDetailPage')),
  },
  // Partner Marketplace (UI-only phase) — browse partner products/services and
  // send quotation inquiries. Permission-less for now, like support-tickets.
  {
    route_type: RouteType.PRIVATE,
    path: '/marketplace',
    component: lazy(() => import('../features/marketplace/pages/MarketplacePage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/marketplace/items/:id',
    component: lazy(() => import('../features/marketplace/pages/MarketplaceItemDetailPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/marketplace/inquiry-cart',
    component: lazy(() => import('../features/marketplace/pages/InquiryCartPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/marketplace/inquiries',
    component: lazy(() => import('../features/marketplace/pages/MyInquiriesPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/marketplace/inquiries/:id',
    component: lazy(() => import('../features/marketplace/pages/InquiryDetailPage')),
  },
  // Partner provider catalogue (hard-coded grid) → provider detail pages.
  {
    route_type: RouteType.PRIVATE,
    path: '/integrations/partners',
    component: lazy(() => import('../features/club-management/pages/PartnerListPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/integrations/partners/workplace',
    component: lazy(() => import('../features/club-management/pages/WorkplacePartnerPage')),
    permission: read(CrmPermissionCode.CrmWorkplaceIntegration),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/club-management/integrations',
    component: lazy(() => import('../features/club-management/pages/IntegrationPage')),
    permission: read(CrmPermissionCode.CrmIntegrations),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/club-management/club-settings',
    component: lazy(() => import('../features/club-management/pages/ClubSettingsPage')),
    permission: read(CrmPermissionCode.CrmClubSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/club-management/club-settings/branch/:id',
    component: lazy(() => import('../features/club-management/pages/BranchDetailPage')),
    permission: read(CrmPermissionCode.CrmClubSettings),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/sale-report',
    component: lazy(() => import('../features/report/pages/SaleReportPage')),
    permission: read(CrmPermissionCode.CrmSaleReport),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/tax-report',
    component: lazy(() => import('../features/report/pages/TaxReportPage')),
    permission: read(CrmPermissionCode.CrmTaxReport),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/credit-report',
    component: lazy(() => import('../features/report/pages/CreditReportPage')),
    feature: 'credit',
    permission: read(CrmPermissionCode.CrmCreditReport),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/quota-report',
    component: lazy(() => import('../features/report/pages/QuotaReportPage')),
    permission: read(CrmPermissionCode.CrmQuotaReport),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/commission-conduct-report',
    component: lazy(() => import('../features/report/pages/CommissionConductReportPage')),
    permission: read(CrmPermissionCode.CrmSaleReport),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/announcement/:id',
    component: lazy(() => import('../features/platform-announcement/pages/AnnouncementDetailPage')),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/lactate-test',
    component: lazy(() => import('../features/lactate-test/pages/LactateTestPage')),
    feature: 'lactateTest',
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/ai-coaching',
    component: lazy(() => import('../features/ai-coaching/pages/AiCoachingPage')),
    feature: 'aiCoaching',
  },
  // Workout (exercise library + workout templates) — feature-flag gated;
  // permission codes come once the backend registers them.
  {
    route_type: RouteType.PRIVATE,
    path: '/workouts/exercises',
    component: lazy(() => import('../features/workout/pages/ExerciseLibraryPage')),
    feature: 'workoutBuilder',
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/workouts/templates',
    component: lazy(() => import('../features/workout/pages/WorkoutTemplateListPage')),
    feature: 'workoutBuilder',
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/workouts/templates/create',
    component: lazy(() => import('../features/workout/pages/WorkoutBuilderPage')),
    feature: 'workoutBuilder',
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/workouts/templates/:id/edit',
    component: lazy(() => import('../features/workout/pages/WorkoutBuilderPage')),
    feature: 'workoutBuilder',
  },
  // Equipment Management
  {
    route_type: RouteType.PRIVATE,
    path: '/equipment-management/assets',
    component: lazy(() => import('../features/equipment-management/pages/AssetListPage')),
    permission: read(CrmPermissionCode.CrmEquipmentList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/equipment-management/assets/:id',
    component: lazy(() => import('../features/equipment-management/pages/AssetDetailPage')),
    permission: read(CrmPermissionCode.CrmEquipmentList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/equipment-management/daily-inspection',
    component: lazy(() => import('../features/equipment-management/pages/DailyInspectionPage')),
    permission: read(CrmPermissionCode.CrmDailyInspection),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/equipment-management/work-orders',
    component: lazy(() => import('../features/equipment-management/pages/WorkOrderListPage')),
    permission: read(CrmPermissionCode.CrmWorkOrders),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/equipment-management/pm-schedule',
    component: lazy(() => import('../features/equipment-management/pages/PMSchedulePage')),
    permission: read(CrmPermissionCode.CrmPMSchedule),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/equipment-management/vendors',
    component: lazy(() => import('../features/equipment-management/pages/VendorListPage')),
    permission: read(CrmPermissionCode.CrmVendorList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/equipment-management/report',
    component: lazy(() => import('../features/equipment-management/pages/EquipmentReportPage')),
    permission: read(CrmPermissionCode.CrmEquipmentReport),
  },
  // Marketing
  {
    route_type: RouteType.PRIVATE,
    path: '/campaigns',
    component: lazy(() => import('../features/campaign/pages/CampaignListPage')),
    permission: read(CrmPermissionCode.CrmCampaignList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/campaigns/:id',
    component: lazy(() => import('../features/campaign/pages/CampaignDetailPage')),
    permission: read(CrmPermissionCode.CrmCampaignList),
  },
  {
    route_type: RouteType.PRIVATE,
    path: '/campaign-performance',
    component: lazy(() => import('../features/campaign/pages/CampaignPerformancePage')),
    permission: read(CrmPermissionCode.CrmCampaignReport),
  },
  // System
  {
    route_type: RouteType.PRIVATE,
    path: '/audit-log',
    component: lazy(() => import('../features/audit-log/pages/AuditLogPage')),
    permission: read(CrmPermissionCode.CrmAuditLog),
  },
]

export default routes
